import { jsx, jsxs } from "react/jsx-runtime";
import * as React from "react";
import { cx } from "../internal/cx.js";
import { useMergedRefs } from "../internal/refs.js";
import { Drawer } from "./Drawer.js";
import { useMediaQuery, useControllableState, DEFAULT_PIN, pinQuery } from "./hooks.js";
import { AppShellProvider, SidebarPlacementProvider } from "./ShellContext.js";
const AppShell = React.forwardRef(function AppShell2({
  app,
  pin = DEFAULT_PIN,
  sidebar,
  topBar,
  bottomNav,
  children,
  mainProps,
  drawerOpen: drawerOpenProp,
  defaultDrawerOpen = false,
  onDrawerOpenChange,
  drawerLabel,
  drawerCloseLabel,
  renderLink,
  className,
  ...rest
}, ref) {
  const pinned = useMediaQuery(pinQuery(pin));
  const [drawerOpen, setDrawerOpen] = useControllableState(drawerOpenProp, defaultDrawerOpen, onDrawerOpenChange);
  const hasDrawer = sidebar != null && sidebar !== false;
  const rootRef = React.useRef(null);
  const setRootRef = useMergedRefs(ref, rootRef);
  const pinnedRef = React.useRef(pinned);
  React.useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);
  React.useEffect(() => {
    if (pinned && drawerOpen) setDrawerOpen(false);
  }, [pinned, drawerOpen, setDrawerOpen]);
  const drawerFinalFocus = React.useCallback(() => {
    const root = rootRef.current;
    if (!pinnedRef.current || !root) return null;
    return root.querySelector('.ev-sidebar [aria-current="page"]') ?? root.querySelector(".ev-sidebar a[href], .ev-sidebar button:not([disabled])");
  }, []);
  const context = React.useMemo(
    () => ({ app, pin, pinned, hasDrawer, drawerOpen, setDrawerOpen, renderLink }),
    [app, pin, pinned, hasDrawer, drawerOpen, setDrawerOpen, renderLink]
  );
  const hasBottomNav = bottomNav != null && bottomNav !== false;
  return /* @__PURE__ */ jsx(AppShellProvider, { value: context, children: /* @__PURE__ */ jsxs(
    "div",
    {
      ref: setRootRef,
      className: cx("ev-shell", "ev-root", className),
      "data-app": app,
      "data-pin": hasDrawer ? pin : void 0,
      "data-bottom-nav": hasBottomNav ? "true" : void 0,
      ...rest,
      children: [
        hasDrawer && (pinned || !drawerOpen) && /* @__PURE__ */ jsx(SidebarPlacementProvider, { value: "sidebar", children: sidebar }),
        /* @__PURE__ */ jsxs("div", { className: "ev-shell__body", children: [
          topBar,
          /* @__PURE__ */ jsx("main", { ...mainProps, className: cx("ev-main", mainProps?.className), children })
        ] }),
        hasBottomNav && bottomNav,
        hasDrawer && /* @__PURE__ */ jsx(Drawer, { label: drawerLabel, closeLabel: drawerCloseLabel, finalFocus: drawerFinalFocus, children: sidebar })
      ]
    }
  ) });
});
export {
  AppShell
};
//# sourceMappingURL=AppShell.js.map
